// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { Link } from "react-router-dom"
import { GuardRokIcon } from "../components/GuardRokLogo"

const CHANNELS = [
  {
    icon: "✉️",
    title: "General Support",
    desc: "Questions about installation, setup, or your account.",
    contact: "support@guardrok.com",
    href: "mailto:support@guardrok.com",
  },
  {
    icon: "🔐",
    title: "Security Issues",
    desc: "Report a vulnerability or security concern responsibly.",
    contact: "security@guardrok.com",
    href: "mailto:security@guardrok.com",
  },
  {
    icon: "💳",
    title: "Billing & Subscriptions",
    desc: "Invoices, plan changes, cancellations, and refunds.",
    contact: "billing@guardrok.com",
    href: "mailto:billing@guardrok.com",
  },
  {
    icon: "🏢",
    title: "Enterprise & Sales",
    desc: "Custom plans, DPAs, BAAs, and volume licensing.",
    contact: "sales@guardrok.com",
    href: "mailto:sales@guardrok.com",
  },
]

const CWS_URL = "https://chromewebstore.google.com/detail/guardrok-ai-firewall/dldblbcpofdflbbhpahbokamikpakhdh"

const FAQ = [
  {
    q: "How do I install the GuardRok extension?",
    a: "Click Add to Chrome from the Chrome Web Store, then sign in with your organization account to activate your policies.",
    link: { text: "Install GuardRok →", href: CWS_URL },
  },
  {
    q: "My prompts are being blocked incorrectly — what do I do?",
    a: "Log in to app.guardrok.com, navigate to Policy Builder, and adjust the sensitivity threshold for the relevant category. For persistent issues, contact support@guardrok.com with your GR-UserID.",
  },
  {
    q: "How do I add my team?",
    a: "From your dashboard, go to Settings → Team and send invite links to your colleagues. Each invite is tied to your organization's policy set.",
  },
  {
    q: "Where is my data stored?",
    a: "All data is stored on secure cloud infrastructure in the United States. Prompt text is never stored — only event metadata (timestamp, risk category, action taken). See our Privacy Policy for full details.",
  },
  {
    q: "Do you offer a Business Associate Agreement (BAA) for HIPAA?",
    a: "Yes. BAAs are available for Professional and Enterprise plans. Contact sales@guardrok.com to initiate the process.",
  },
  {
    q: "How do I cancel my subscription?",
    a: "Go to Settings → Billing inside your dashboard and select Cancel Plan. Your access continues until the end of the current billing period. For help, email billing@guardrok.com.",
  },
]

export default function Support() {
  return (
    <div style={{ minHeight: "100vh", background: "#030712", color: "#fff", fontFamily: "system-ui, sans-serif" }}>

      {/* Nav */}
      <nav style={{ padding: "20px 48px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #0f172a", position: "sticky", top: 0, background: "rgba(3,9,18,0.95)", backdropFilter: "blur(12px)", zIndex: 100 }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
          <GuardRokIcon size={36} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.5px", lineHeight: 1, color: "#fff" }}>Guard<span style={{ color: "#E63946" }}>Rok</span><span style={{ fontSize: 8, color: "#6b7280", verticalAlign: "super", marginLeft: 1 }}>™</span></div>
            <div style={{ fontSize: 9, letterSpacing: "3px", color: "#6b7280", textTransform: "uppercase" as const, marginTop: 2 }}>AI Firewall</div>
          </div>
        </Link>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Link to="/docs"    style={{ color: "#9ca3af", fontSize: 14, textDecoration: "none" }}>Docs</Link>
          <Link to="/pricing" style={{ color: "#9ca3af", fontSize: 14, textDecoration: "none" }}>Pricing</Link>
          <Link to="/login"   style={{ color: "#9ca3af", fontSize: 14, textDecoration: "none" }}>Sign in</Link>
        </div>
      </nav>

      {/* Hero */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "64px 40px 0" }}>
        <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-1px", marginBottom: 12 }}>Support</h1>
        <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.7, marginBottom: 56 }}>
          We're here to help. Choose the right channel below or browse the FAQ — most questions are answered in under 24 hours.
        </p>

        {/* Contact cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 72 }}>
          {CHANNELS.map(ch => (
            <a
              key={ch.title}
              href={ch.href}
              style={{
                display: "block", textDecoration: "none",
                background: "#0a0f1a", border: "1px solid #1e293b",
                borderRadius: 12, padding: "24px 28px",
                transition: "border-color 0.2s",
              }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = "#E63946")}
              onMouseLeave={e => (e.currentTarget.style.borderColor = "#1e293b")}
            >
              <div style={{ fontSize: 28, marginBottom: 10 }}>{ch.icon}</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: "#f1f5f9" }}>{ch.title}</div>
              <div style={{ color: "#64748b", fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>{ch.desc}</div>
              <div style={{ color: "#E63946", fontSize: 13, fontWeight: 600 }}>{ch.contact}</div>
            </a>
          ))}
        </div>

        {/* FAQ */}
        <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.5px", marginBottom: 32 }}>Frequently Asked Questions</h2>
        <div style={{ display: "flex", flexDirection: "column" as const, gap: 0 }}>
          {FAQ.map((item, i) => (
            <div
              key={i}
              style={{
                borderTop: "1px solid #0f172a",
                padding: "24px 0",
                ...(i === FAQ.length - 1 ? { borderBottom: "1px solid #0f172a" } : {}),
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10, color: "#f1f5f9" }}>{item.q}</div>
              <div style={{ color: "#64748b", fontSize: 14, lineHeight: 1.8 }}>{item.a}</div>
              {"link" in item && item.link && (
                <a href={(item as any).link.href} target="_blank" rel="noopener noreferrer"
                  style={{ display: "inline-block", marginTop: 8, color: "#E63946", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>
                  {(item as any).link.text}
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Footer note */}
        <div style={{ marginTop: 64, padding: "24px 28px", background: "#0a0f1a", borderRadius: 12, border: "1px solid #1e293b" }}>
          <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.8 }}>
            <span style={{ color: "#94a3b8", fontWeight: 600 }}>Response times: </span>
            General support — within 24 hours · Security issues — within 4 hours · Enterprise — dedicated SLA per contract.
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "48px 40px 64px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #0f172a", marginTop: 64 }}>
        <div style={{ fontSize: 12, color: "#374151" }}>© 2026 GuardRok™ · aiApas LLC</div>
        <div style={{ display: "flex", gap: 24 }}>
          <Link to="/privacy" style={{ color: "#374151", fontSize: 12, textDecoration: "none" }}>Privacy</Link>
          <Link to="/terms"   style={{ color: "#374151", fontSize: 12, textDecoration: "none" }}>Terms</Link>
          <Link to="/cookies" style={{ color: "#374151", fontSize: 12, textDecoration: "none" }}>Cookies</Link>
        </div>
      </div>

    </div>
  )
}
